import { Injectable } from '@angular/core';
import { ProdutoCardapio, produtoCardapioDoProduto, raizDaCategoria, slug } from '@mobile/helpers/cardapio-legado.helper';
import { estaVigente, melhorPromocao } from '@mobile/helpers/combo-promocao.helper';
import { vigenteNoDia, hojeSaoPaulo } from '@mobile/helpers/vigencia.helper';
import { Categoria, Produto, TabelaPreco, Tamanho } from '@mobile/models/cardapio/cardapio.model';
import { IAdicionalStatus, IBorda } from '@mobile/models/produtos/produto.model';

import { CardapioLegadoFirebaseService } from '../firebase/cardapio-legado-firebase.service';
import { CategoriasFirebaseService } from '../firebase/categorias-firebase.service';
import { ProdutosFirebaseService } from '../firebase/produtos-firebase.service';
import { TabelasPrecoFirebaseService } from '../firebase/tabelas-preco-firebase.service';

const CARDAPIO_CACHE_MS = 60_000;

export interface ModeloCardapio {
  categorias: Categoria[];
  tabelas: TabelaPreco[];
  produtos: Produto[];
}

/** Tamanhos de pizza que o pedido (price, price_broto, price_trem, price_bi_trem) já sabe representar. */
const TAMANHOS_PIZZA_SUPORTADOS = ['broto', 'normal', 'trem', 'bi-trem'];

const RAIZ_PIZZAS = 'pizzas';

/** O que a loja anuncia para o dia de hoje (promoções e combos que valem em algum horário). */
export interface DestaquesDoDia {
  /** Ex.: 'quinta-feira'. */
  dia: string;
  promocoes: Produto[];
  combos: Produto[];
  /** Quantos combos já podem ser pedidos neste instante. */
  combosDisponiveisAgora: number;
}

export type ModoEscolha = 'unico' | 'opcional' | 'obrigatorio';

/** Uma aba do cardápio: um tamanho de uma categoria raiz (Broto, Pizza, Trem, Esfirrões, Bebidas...). */
export interface AbaCardapio {
  rota: string;
  rotulo: string;
  categoriaId: string;
  tamanhoId: string;
  /** Sabores do tamanho: 1 inteiro, 2 meio-a-meio, 4 trem, 6 bi-trem. */
  partes: number;
  /** unico: item inteiro; opcional: meio-a-meio a critério do cliente; obrigatorio: sempre `partes` sabores. */
  modo: ModoEscolha;
  /** Aba de combos (não tem categoria nem tamanho). */
  combos?: boolean;
}

export const ROTA_COMBOS = 'combos';

export interface GrupoCardapio {
  titulo: string;
  itens: ProdutoCardapio[];
}

export interface SecaoCardapio {
  titulo: string;
  grupos: GrupoCardapio[];
}

function modoDoTamanho(partes: number): ModoEscolha {
  if (partes <= 1) return 'unico';
  return partes === 2 ? 'opcional' : 'obrigatorio';
}

function tamanhoSuportado(tamanho: Tamanho): boolean {
  return tamanho.ativo && (tamanho.partes <= 1 || TAMANHOS_PIZZA_SUPORTADOS.includes(tamanho.id));
}

function codigoDeOrdenacao(item: ProdutoCardapio): string | number {
  return item.codigo_interno ?? (+item.name.split('-')[0] || item.name);
}

function porCodigoOuNome(a: ProdutoCardapio, b: ProdutoCardapio): number {
  const codeA = codigoDeOrdenacao(a);
  const codeB = codigoDeOrdenacao(b);
  return codeA > codeB ? 1 : codeA < codeB ? -1 : 0;
}

function semAcento(texto: string): string {
  return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

@Injectable({ providedIn: 'root' })
export class CardapioService {
  private cache?: { lido: number; modelo: Promise<ModeloCardapio> };

  constructor(
    private categoriasService: CategoriasFirebaseService,
    private tabelasService: TabelasPrecoFirebaseService,
    private produtosService: ProdutosFirebaseService,
    private legadoService: CardapioLegadoFirebaseService,
  ) {}

  /** Categorias, tabelas de preço e produtos, com cache curto. */
  getModeloCardapio(): Promise<ModeloCardapio> {
    if (!this.cache || Date.now() - this.cache.lido > CARDAPIO_CACHE_MS) {
      const modelo = Promise.all([
        this.categoriasService.getAll(),
        this.tabelasService.getAll(),
        this.produtosService.getAll(),
      ]).then(([categorias, tabelas, produtos]) => ({ categorias, tabelas, produtos }));
      this.cache = { lido: Date.now(), modelo };
      modelo.catch(() => (this.cache = undefined));
    }
    return this.cache.modelo;
  }

  getBordas(): Promise<IBorda[]> {
    return this.getItensDaCategoria<IBorda>('bordas');
  }

  getAdicionais(): Promise<IAdicionalStatus[]> {
    return this.getItensDaCategoria<IAdicionalStatus>('adicionais');
  }

  /** Lê do modelo novo; cai no `Cardapio/pizzas/{tipo}` antigo se o modelo estiver vazio ou der erro. */
  private async getItensDaCategoria<T>(tipo: 'bordas' | 'adicionais'): Promise<T[]> {
    try {
      const { itens } = await this.carregar();
      if (itens.length) {
        return itens.filter(i => i.type === tipo) as unknown as T[];
      }
    } catch (err) {
      console.error('cardápio: falha ao ler o modelo novo, usando o legado', err);
    }
    return this.legadoService.getItens<T>('pizzas', tipo);
  }

  /** Combos ativos, em estoque e dentro da vigência, destaques primeiro. */
  async getCombos(): Promise<Produto[]> {
    const { produtos } = await this.getModeloCardapio();
    const agora = new Date();
    return produtos
      .filter(p => p.tipo === 'combo' && p.ativo && p.em_estoque !== false && estaVigente(p.vigencia, agora)
        && ((p.itensFixos?.length ?? 0) > 0 || (p.grupos?.length ?? 0) > 0))
      .sort((a, b) => Number(!!b.destaque) - Number(!!a.destaque) || a.nome.localeCompare(b.nome, 'pt-BR'));
  }

  async getDestaquesDoDia(): Promise<DestaquesDoDia> {
    const { produtos } = await this.getModeloCardapio();
    const agora = new Date();
    const combos = produtos
      .filter(p => p.tipo === 'combo' && p.ativo && p.em_estoque !== false && vigenteNoDia(p.vigencia, agora)
        && ((p.itensFixos?.length ?? 0) > 0 || (p.grupos?.length ?? 0) > 0))
      .sort((a, b) => Number(!!b.destaque) - Number(!!a.destaque) || a.nome.localeCompare(b.nome, 'pt-BR'));
    const promocoes = produtos
      .filter(p => p.tipo === 'promocao' && p.ativo && !!p.desconto && vigenteNoDia(p.vigencia, agora))
      .sort((a, b) => Number(!!b.destaque) - Number(!!a.destaque) || a.nome.localeCompare(b.nome, 'pt-BR'));

    return {
      dia: hojeSaoPaulo(agora).nome,
      promocoes,
      combos,
      combosDisponiveisAgora: combos.filter(c => estaVigente(c.vigencia, agora)).length,
    };
  }

  async getAbas(): Promise<AbaCardapio[]> {
    const { categorias, itens } = await this.carregar();
    const abas: AbaCardapio[] = [];

    if ((await this.getCombos()).length) {
      abas.push({ rota: ROTA_COMBOS, rotulo: 'Combos', categoriaId: '', tamanhoId: '', partes: 1, modo: 'unico', combos: true });
    }

    const raizes = categorias
      .filter(c => !c.parentId && c.ativo && !c.tamanhosDe)
      .sort((a, b) => a.ordem - b.ordem);

    for (const raiz of raizes) {
      const tamanhos = (raiz.tamanhos ?? []).filter(tamanhoSuportado).sort((a, b) => a.ordem - b.ordem);

      for (const tamanho of tamanhos) {
        const temProduto = itens.some(i => this.raizId(i, categorias) === raiz.id && i.precos[tamanho.id] !== undefined);
        if (!temProduto) continue;

        const unico = tamanho.id === 'unico';
        // Só a raiz de pizzas usa o rótulo do tamanho puro; as demais levam o nome da categoria.
        const rotulo = unico ? raiz.nome : raiz.id === RAIZ_PIZZAS ? tamanho.rotulo : `${raiz.nome} - ${tamanho.rotulo}`;
        abas.push({
          rota: slug(rotulo),
          rotulo,
          categoriaId: raiz.id,
          tamanhoId: tamanho.id,
          partes: tamanho.partes,
          modo: modoDoTamanho(tamanho.partes),
        });
      }
    }

    return abas;
  }

  /** Aba aberta quando a URL não indica nenhuma. */
  abaPadrao(abas: AbaCardapio[]): AbaCardapio | undefined {
    return abas.find(a => a.categoriaId === RAIZ_PIZZAS && a.tamanhoId === 'normal') ?? abas[0];
  }

  /** Produtos da aba, agrupados por categoria (seção) e por tabela de preço (grupo). */
  async getSecoes(aba: AbaCardapio): Promise<SecaoCardapio[]> {
    const { categorias, itens } = await this.carregar();
    const daAba = itens.filter(i => this.raizId(i, categorias) === aba.categoriaId && i.precos[aba.tamanhoId] !== undefined);

    const secoes = new Map<string, { categoria: Categoria; itens: ProdutoCardapio[] }>();
    for (const item of daAba) {
      const categoria = categorias.find(c => c.id === item.categoriaId);
      if (!categoria) continue;
      const secao = secoes.get(categoria.id) ?? { categoria, itens: [] };
      secao.itens.push(item);
      secoes.set(categoria.id, secao);
    }

    return [...secoes.values()]
      .sort((a, b) => this.compararCaminho(a.categoria, b.categoria, categorias))
      .map(({ categoria, itens: itensDaSecao }) => ({
        titulo: categoria.nome,
        grupos: this.agruparPorTabela(itensDaSecao.sort(porCodigoOuNome)),
      }));
  }

  filtrar(secoes: SecaoCardapio[], texto?: string | null): SecaoCardapio[] {
    const busca = semAcento((texto ?? '').trim());
    if (!busca) return secoes;

    return secoes
      .map(secao => ({
        ...secao,
        grupos: secao.grupos
          .map(grupo => ({
            ...grupo,
            itens: grupo.itens.filter(i => semAcento(`${i.codigo_interno ?? ''} ${i.name} ${i.description ?? ''}`).includes(busca)),
          }))
          .filter(grupo => grupo.itens.length),
      }))
      .filter(secao => secao.grupos.length);
  }

  private async carregar(): Promise<{ categorias: Categoria[]; itens: ProdutoCardapio[] }> {
    const { categorias, tabelas, produtos } = await this.getModeloCardapio();
    const agora = new Date();
    const promocoes = produtos.filter(p => p.tipo === 'promocao' && p.ativo);
    const itens = produtos
      .map(produto => {
        const item = produtoCardapioDoProduto(produto, categorias, tabelas);
        return item && promocoes.length ? this.comPromocoes(item, produto, promocoes, categorias, agora) : item;
      })
      .filter((item): item is ProdutoCardapio => !!item && item.ativo !== false);
    return { categorias, itens };
  }

  /** Troca o preço de cada tamanho pelo da melhor promoção vigente, guardando o original. */
  private comPromocoes(
    item: ProdutoCardapio,
    produto: Produto,
    promocoes: Produto[],
    categorias: Categoria[],
    agora: Date,
  ): ProdutoCardapio {
    const precos = { ...item.precos };
    const aplicadas: NonNullable<ProdutoCardapio['promocoes']> = {};

    for (const tamanhoId of Object.keys(precos)) {
      const precoOriginal = precos[tamanhoId];
      if (!(precoOriginal > 0)) continue;
      const aplicada = melhorPromocao(promocoes, produto, precoOriginal, tamanhoId, categorias, agora);
      if (!aplicada) continue;
      precos[tamanhoId] = aplicada.preco;
      aplicadas[tamanhoId] = {
        id: aplicada.promocao.id,
        nome: aplicada.promocao.nome,
        destaque: !!aplicada.promocao.destaque,
        precoOriginal,
      };
    }
    if (!Object.keys(aplicadas).length) return item;

    const promovido: ProdutoCardapio = { ...item, precos, promocoes: aplicadas };
    promovido.price = precos.normal ?? precos.unico ?? Object.values(precos)[0] ?? 0;
    if (precos.broto !== undefined) promovido.price_broto = precos.broto;
    if (precos.trem !== undefined) promovido.price_trem = precos.trem;
    if (precos['bi-trem'] !== undefined) promovido.price_bi_trem = precos['bi-trem'];
    return promovido;
  }

  private raizId(item: ProdutoCardapio, categorias: Categoria[]): string | undefined {
    const categoria = categorias.find(c => c.id === item.categoriaId);
    return categoria ? raizDaCategoria(categoria, categorias).id : undefined;
  }

  /** Ordens da raiz até a categoria, para ordenar seções respeitando a hierarquia. */
  private caminho(categoria: Categoria, categorias: Categoria[]): number[] {
    const ordens: number[] = [];
    let atual: Categoria | undefined = categoria;
    while (atual) {
      ordens.unshift(atual.ordem);
      const paiId = atual.parentId;
      atual = paiId ? categorias.find(c => c.id === paiId) : undefined;
    }
    return ordens;
  }

  private compararCaminho(a: Categoria, b: Categoria, categorias: Categoria[]): number {
    const caminhoA = this.caminho(a, categorias);
    const caminhoB = this.caminho(b, categorias);
    for (let i = 0; i < Math.max(caminhoA.length, caminhoB.length); i++) {
      const diff = (caminhoA[i] ?? -1) - (caminhoB[i] ?? -1);
      if (diff) return diff;
    }
    return 0;
  }

  private agruparPorTabela(itens: ProdutoCardapio[]): GrupoCardapio[] {
    const grupos = new Map<string, ProdutoCardapio[]>();
    for (const item of itens) {
      grupos.set(item.custom_type, [...(grupos.get(item.custom_type) ?? []), item]);
    }
    return [...grupos.entries()].map(([titulo, lista]) => ({ titulo, itens: lista }));
  }
}
